/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback } from 'react';

import { jsx, keyframes, cssMap } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import Heading from '@atlaskit/heading';
import { IconTile } from '@atlaskit/icon';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import CrossIcon from '@atlaskit/icon/core/cross';
import DeleteIcon from '@atlaskit/icon/core/delete';
import LockLockedIcon from '@atlaskit/icon/core/lock-locked';
import PanelRightIcon from '@atlaskit/icon/core/panel-right';
import { RovoIcon } from '@atlaskit/logo';
import { Flex, Inline, Text, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import type { Suggestion } from '../../../aiSuggestionsPluginType';

type SuggestionCardProps = {
	isShowingOnlySuggestion?: boolean;
	onAccept?: () => void;
	onClose?: () => void;
	onDiscard?: () => void;
	onNavigate?: (direction: 'up' | 'down') => void;
	onShowSidebar?: () => void;
	onToggleOnlySuggestion?: () => void;
	stackSize?: number;
	suggestion: Suggestion;
};

const stackTiltIn1 = keyframes({
	from: { transform: 'rotate(0deg)' },
	to: { transform: 'rotate(3deg)' },
});

const stackTiltIn2 = keyframes({
	from: { transform: 'rotate(0deg)' },
	to: { transform: 'rotate(6deg)' },
});

const styles = cssMap({
	sourceButton: {
		display: 'flex',
		backgroundColor: 'transparent',
		alignItems: 'center',
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		gap: token('space.100'),
		color: token('color.text.subtle'),
	},
	card: {
		position: 'relative',
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.xlarge'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		boxShadow: token('elevation.shadow.overlay'),
		width: '280px',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.200'),
		boxSizing: 'border-box',
	},
	cardStack: {
		'&::before': {
			position: 'absolute',
			content: '""',
			backgroundColor: token('elevation.surface'),
			boxShadow: token('elevation.shadow.overlay'),
			borderRadius: token('radius.xlarge'),
			bottom: 0,
			left: 0,
			width: `calc(100% - ${token('space.100')})`,
			height: 48,
			transformOrigin: '0% 100%',
			zIndex: -1,
			animationName: stackTiltIn1,
			animationDuration: token('motion.duration.medium', '200ms'),
			animationTimingFunction: token('motion.easing.out.practical', 'ease-out'),
			animationFillMode: 'forwards',
		},
		'@media (prefers-reduced-motion: reduce)': {
			'&::before': {
				animation: 'none',
				transform: 'rotate(3deg)',
			},
		},
	},
	additionalCardStack: {
		'&::after': {
			position: 'absolute',
			content: '""',
			backgroundColor: token('elevation.surface'),
			boxShadow: token('elevation.shadow.overlay'),
			borderRadius: token('radius.xlarge'),
			bottom: 0,
			left: 0,
			width: `calc(100% - ${token('space.200')})`,
			height: 48,
			transformOrigin: '0% 100%',
			zIndex: -2,
			animationName: stackTiltIn2,
			animationDuration: token('motion.duration.medium', '200ms'),
			animationTimingFunction: token('motion.easing.out.practical', 'ease-out'),
			animationFillMode: 'forwards',
		},
	},
});

export const SuggestionCard: React.ForwardRefExoticComponent<
	SuggestionCardProps & React.RefAttributes<HTMLDivElement>
> = React.forwardRef<HTMLDivElement, SuggestionCardProps>(function SuggestionCard(
	{
		onAccept,
		onDiscard,
		onNavigate,
		onClose,
		onShowSidebar,
		// onToggleOnlySuggestion,
		// isShowingOnlySuggestion,
		suggestion,
		stackSize = 1,
	},
	ref,
): React.JSX.Element {
	const { formatMessage } = useIntl();
	const [isSourcesExpanded, setIsSourcesExpanded] = React.useState(false);

	const handleSourcesToggle = useCallback(() => {
		setIsSourcesExpanded((previous) => !previous);
	}, []);

	return (
		<div
			ref={ref}
			css={[
				styles.card,
				stackSize > 1 && styles.cardStack,
				stackSize > 2 && styles.additionalCardStack,
			]}
		>
			<Flex alignItems="center" justifyContent="space-between">
				<Inline space="space.0" alignBlock="center">
					<IconButton
						/* eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed) */
						icon={() => (
							<ChevronUpIcon
								label={formatMessage(aiSuggestionsMessages.cardExpandButtonLabel)}
								size="small"
							/>
						)}
						label={formatMessage(aiSuggestionsMessages.cardExpandButtonLabel)}
						appearance="subtle"
						spacing="compact"
						isDisabled={!onNavigate}
						onClick={onNavigate ? () => onNavigate('up') : undefined}
					/>
					<IconButton
						/* eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed) */
						icon={() => (
							<ChevronDownIcon
								label={formatMessage(aiSuggestionsMessages.cardCollapseButtonLabel)}
								size="small"
							/>
						)}
						label={formatMessage(aiSuggestionsMessages.cardCollapseButtonLabel)}
						appearance="subtle"
						spacing="compact"
						isDisabled={!onNavigate}
						onClick={onNavigate ? () => onNavigate('down') : undefined}
					/>
				</Inline>
				<Inline space="space.050" alignBlock="center">
					{onShowSidebar && (
						<IconButton
							icon={PanelRightIcon}
							label={formatMessage(aiSuggestionsMessages.cardShowSidebarButtonLabel)}
							appearance="subtle"
							spacing="compact"
							onClick={onShowSidebar}
						/>
					)}
					<IconButton
						icon={CrossIcon}
						label={formatMessage(aiSuggestionsMessages.cardCloseButtonLabel)}
						appearance="subtle"
						spacing="compact"
						onClick={onClose}
					/>
				</Inline>
			</Flex>

			<Inline space="space.100" alignBlock="center">
				<RovoIcon
					label={formatMessage(aiSuggestionsMessages.cardRovoTitle)}
					size="small"
					shouldUseNewLogoDesign
					shouldUseHexLogo
				/>
				<Heading size="xsmall">{formatMessage(aiSuggestionsMessages.cardRovoTitle)}</Heading>
				<Tooltip content={formatMessage(aiSuggestionsMessages.cardPrivateTooltipLabel)}>
					<IconTile
						icon={LockLockedIcon}
						label={formatMessage(aiSuggestionsMessages.cardPrivateLabel)}
						appearance="gray"
						size="xsmall"
					/>
				</Tooltip>
			</Inline>
			<Text as="p" color="color.text">
				{suggestion.description}
			</Text>
			<Flex alignItems="start" direction="column" gap="space.100">
				<Pressable xcss={styles.sourceButton} onClick={handleSourcesToggle}>
					{isSourcesExpanded
						? formatMessage(aiSuggestionsMessages.cardSourcesHideButtonLabel)
						: formatMessage(aiSuggestionsMessages.cardSourcesShowButtonLabel)}

					{isSourcesExpanded ? (
						<ChevronDownIcon label="" size="small" />
					) : (
						<ChevronRightIcon label="" size="small" />
					)}
				</Pressable>
				{isSourcesExpanded && (
					<Text as="p" color="color.text.subtle">
						{suggestion.reasoning}
					</Text>
				)}
			</Flex>
			<Flex alignItems="center" justifyContent="space-between" gap="space.100">
				<Inline space="space.050" alignBlock="center">
					<Button iconBefore={CheckMarkIcon} appearance="primary" onClick={onAccept}>
						{formatMessage(aiSuggestionsMessages.cardAcceptButtonLabel)}
					</Button>
					<Button iconBefore={DeleteIcon} appearance="subtle" onClick={onDiscard}>
						{formatMessage(aiSuggestionsMessages.cardDiscardButtonLabel)}
					</Button>
				</Inline>
			</Flex>
		</div>
	);
});
